import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
  Chip,
} from '@mui/material';
import { Login as LoginIcon, PictureAsPdf as PdfIcon, Send as SendIcon, CompareArrows as CompareArrowsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { ContestFrameShell, ContestSelfEvalScorecardPanel } from '@components/contest/PublicContestFrames';
import ContestLeaderboardTabPanel from '@components/contest/ContestLeaderboardTabPanel';
import { brandColors, getGradeColor } from '@config/brandTokens';
import {
  CONTEST_TYPE_OPTIONS,
  hasContestCatalogPreview,
  isProfileContestAge,
} from '@utils/contestCatalog';
import {
  areAllSelfEvalRowsSaved,
  assessEntryValue,
  clearContestSelfEvalDraft,
  consumeSelfEvalResumeIfMatch,
  countSelfEvalSkillsOnDate,
  contestSelfEvalQuarterLabel,
  createEmptyContestSelfEvalDraft,
  isSelfEvalLockedThisQuarter,
  isSelfEvalRowComplete,
  listFilledSelfEvalSkills,
  loadContestSelfEvalDraft,
  loadSavedContestSelfEval,
  MAX_SELF_EVAL_SKILLS_PER_DATE,
  persistContestSelfEvalForUser,
  pickSelfEvalHydration,
  reassessSelfEvalDraftGrades,
  rememberSelfEvalResume,
  resolveSelfEvalConsentRole,
  saveContestSelfEvalDraft,
  uniqueCatalogContests,
  type ContestSelfEvalConsentRecord,
  type ContestSelfEvalDraft,
  type ContestSelfEvalEntry,
  type SelfEvalGradeChange,
} from '@utils/contestSelfEvaluation';
import {
  fetchPlayerContestSubmissionsByCatalog,
  formatContestAdminScore,
  formatContestSubmittedAt,
} from '@utils/contestSubmissionStatus';
import { resolveFoundationCertification, foundationCertificateNo } from '@utils/contestCertification';
import { generateFoundationCertificatePDF } from '@utils/generateCertificatesPDF';
import { useFoundationCatalogGroups } from '@hooks/useFoundationCatalogGroups';
import { useActiveSession } from '@hooks/useActiveSession';
import { useAuthStore } from '@store/authStore';
import authService from '@services/authService';
import api from '@services/api';
import ContestSelfEvalConsentDialog from '@components/contest/ContestSelfEvalConsentDialog';
import ContestResultsCompareDialog, {
  type ContestCompareSkillRow,
} from '@components/contest/ContestResultsCompareDialog';

export interface ContestSelfEvalTabPanelProps {
  sportName: string;
  ageCategory: string;
  genderKey?: import('../../types/contestCatalog').ContestGenderKey;
  onLoginRequired?: (returnPath: string) => void;
  loginReturnPath: string;
  playerId?: string;
  playerName?: string;
  actingKind?: 'self' | 'child';
  viewOnly?: boolean;
  missingDob?: boolean;
  eligibleAgeCategory?: string;
  profileSlot?: React.ReactNode;
}

type SelfEvalPanel = 'scorecard' | 'standings';

const ContestSelfEvalTabPanel: React.FC<ContestSelfEvalTabPanelProps> = ({
  sportName,
  ageCategory,
  genderKey = 'boys',
  onLoginRequired,
  loginReturnPath,
  playerId,
  playerName,
  actingKind,
  viewOnly = false,
  missingDob = false,
  eligibleAgeCategory,
  profileSlot,
}) => {
  const { signedIn: sessionSignedIn, user: sessionUser } = useActiveSession();
  const authUser = useAuthStore((s) => s.user);
  const authIsAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const signedIn =
    Boolean(authIsAuthenticated && (authUser?.id || authService.hasActiveSession())) || sessionSignedIn;
  const user = authUser || sessionUser;
  const { categoryGroups, allContests: catalogContests, loading: catalogLoading, hasContests } =
    useFoundationCatalogGroups(sportName);
  const allContests = useMemo(() => uniqueCatalogContests(catalogContests), [catalogContests]);
  const hasPreview = hasContests || hasContestCatalogPreview(sportName);

  const [panel, setPanel] = useState<SelfEvalPanel>('scorecard');
  const [draft, setDraft] = useState<ContestSelfEvalDraft>(() =>
    createEmptyContestSelfEvalDraft(sportName, ageCategory)
  );
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<'save' | 'submit' | null>(null);
  const [pendingSaveContestId, setPendingSaveContestId] = useState<string | null>(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [benchmarkRevision, setBenchmarkRevision] = useState<SelfEvalGradeChange[] | null>(null);
  const [certificateOpen, setCertificateOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [contestSubmissions, setContestSubmissions] = useState<
    Record<string, import('@utils/contestSubmissionStatus').PlayerContestSubmissionRow>
  >({});

  const catalogIds = useMemo(() => allContests.map((contest) => contest.id), [allContests]);
  const skillCount = allContests.length;
  const filledSkills = listFilledSelfEvalSkills(draft, allContests);
  const allRowsSaved = areAllSelfEvalRowsSaved(draft, catalogIds);
  const lockedThisQuarter = isSelfEvalLockedThisQuarter(draft.status, draft.submittedAt);
  const currentQuarterLabel = contestSelfEvalQuarterLabel();
  const scorecardLocked = viewOnly || lockedThisQuarter || missingDob;
  const consentSkills = useMemo(() => {
    const rows = allContests.map((contest, index) => ({
      contestId: contest.id,
      title: contest.title,
      index: index + 1,
    }));
    if (pendingAction === 'save' && pendingSaveContestId) {
      return rows.filter((skill) => skill.contestId === pendingSaveContestId);
    }
    return rows;
  }, [allContests, pendingAction, pendingSaveContestId]);
  const consentRole = resolveSelfEvalConsentRole(user, actingKind);
  const ownerKey = playerId || (user?.id ? String(user.id) : '');
  const displayPlayerName =
    playerName || [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || '';
  const canWriteThisAge = isProfileContestAge(eligibleAgeCategory, ageCategory);
  const ageMismatchMessage = eligibleAgeCategory
    ? `Save and submit for ${displayPlayerName || 'this player'} only in ${eligibleAgeCategory}. You can still view ${ageCategory}.`
    : '';
  const saveDisabledReason = viewOnly
    ? ''
    : missingDob
      ? 'Add date of birth on this profile first'
      : lockedThisQuarter
      ? `Already submitted for ${currentQuarterLabel}`
      : !canWriteThisAge
        ? `Save only in ${eligibleAgeCategory}`
        : !allRowsSaved
          ? 'Save each skill, then submit'
          : '';
  const guidelineContest =
    allContests.find((contest) => contest.id === pendingSaveContestId) ||
    allContests.find((contest) => contest.id === filledSkills[0]?.contestId) ||
    allContests[0] ||
    null;
  const typeLabel =
    CONTEST_TYPE_OPTIONS.find((o) => o.id === 'foundation')?.label || 'Developmental & Performance Capabilities';
  const foundationLabel = `${typeLabel}${allContests.length ? ` (${allContests.length} skill${allContests.length === 1 ? '' : 's'})` : ''}`;
  const recordedScoreRows = useMemo(
    () =>
      allContests
        .map((contest) => ({
          contest,
          entry: draft.entries[contest.id],
        }))
        .filter((row) => String(row.entry?.value || '').trim() !== ''),
    [allContests, draft.entries],
  );

  useEffect(() => {
    let cancelled = false;
    if (!playerId || allContests.length === 0) {
      setContestSubmissions({});
      return () => {
        cancelled = true;
      };
    }
    void fetchPlayerContestSubmissionsByCatalog(sportName, playerId, allContests, ageCategory).then((rows) => {
      if (!cancelled) setContestSubmissions(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [playerId, sportName, allContests, ageCategory]);

  const compareRows = useMemo((): ContestCompareSkillRow[] => {
    return allContests.map((contest) => {
      const selfEntry = draft.entries[contest.id];
      const contestRow = contestSubmissions[contest.id];
      const contestValue = contestRow ? formatContestAdminScore(contestRow.score, contestRow.status) : '';
      return {
        title: contest.title,
        selfValue: String(selfEntry?.value || ''),
        selfGrade: selfEntry?.grade ?? null,
        selfDate: String(selfEntry?.recordedDate || ''),
        contestValue,
        contestGrade: contestValue ? assessEntryValue(contest, ageCategory, contestValue, genderKey) : null,
        contestRecordedDate: contestRow?.recordedDate || '',
        contestSubmittedDate: contestRow?.submitted ? formatContestSubmittedAt(contestRow.createdAt) : '',
        contestStatus: contestRow?.status || '',
      };
    });
  }, [allContests, draft.entries, contestSubmissions, ageCategory, genderKey]);
  const foundationCert = useMemo(
    () => resolveFoundationCertification(draft, catalogIds, allContests),
    [draft, catalogIds, allContests],
  );
  const certificatePlayer = {
    firstName: displayPlayerName || user?.firstName || user?.email || 'Player',
    lastName: displayPlayerName ? '' : user?.lastName || '',
    playerIdCode: user?.playerIdCode || playerId || 'N/A',
    id: playerId,
  };
  const handleDownloadFoundationCert = () => {
    if (!foundationCert) return;
    const certNo = foundationCertificateNo(foundationCert, certificatePlayer.playerIdCode);
    void generateFoundationCertificatePDF({
      player: certificatePlayer,
      sport: foundationCert.sport,
      ageCategory: foundationCert.ageCategory,
      levelCode: foundationCert.levelCode,
      levelLabel: foundationCert.levelLabel,
      scorePercent: foundationCert.scorePercent,
      skillsAssessed: foundationCert.gradedCount,
      certificateNo: certNo,
      issuedOn: foundationCert.updatedAt
        ? new Date(foundationCert.updatedAt).toLocaleDateString()
        : new Date().toLocaleDateString(),
    });
  };

  const hydrateKey = `${signedIn && user?.id ? String(user.id) : 'guest'}:${ownerKey}:${sportName}:${ageCategory}`;

  useEffect(() => {
    if (!hasPreview) return;
    let cancelled = false;

    const hydrate = async () => {
      const guest = loadContestSelfEvalDraft(sportName, ageCategory, ownerKey || undefined);
      if (!signedIn || !user?.id) {
        setDraft(guest);
        return;
      }

      let remote: ContestSelfEvalDraft | null = null;
      try {
        const response = await api.get(
          `/contest-catalog/${encodeURIComponent(sportName)}/self-eval`,
          { params: { age: ageCategory, playerId: playerId || undefined }, skipErrorToast: true },
        );
        const payload = response.data?.data;
        if (payload?.kind === 'contest_self_evaluation' || payload?.entries) {
          remote = {
            ...createEmptyContestSelfEvalDraft(sportName, ageCategory),
            ...payload,
            sport: sportName,
            ageCategory,
            kind: 'contest_self_evaluation',
            entries: Object.fromEntries(
              Object.entries(payload.entries || {}).map(([id, raw]) => {
                const entry = { contestId: id, ...(raw as ContestSelfEvalEntry) };
                const alreadyStored = payload.status === 'saved' || payload.status === 'submitted';
                if (alreadyStored && isSelfEvalRowComplete(entry) && !entry.savedAt) {
                  entry.savedAt = payload.submittedAt || payload.updatedAt || new Date().toISOString();
                }
                return [id, entry];
              }),
            ),
            status: payload.status || (payload.entries ? 'saved' : 'draft'),
            submittedAt: payload.submittedAt,
          };
        }
      } catch {
        remote = null;
      }
      if (cancelled) return;

      const saved =
        (ownerKey && loadSavedContestSelfEval(ownerKey, sportName, ageCategory)) ||
        (actingKind !== 'child' && user?.id
          ? loadSavedContestSelfEval(String(user.id), sportName, ageCategory)
          : null);
      const chosen = pickSelfEvalHydration({ remote, saved, guest, sport: sportName, ageCategory });
      setDraft(chosen);
      persistContestSelfEvalForUser(ownerKey || String(user.id), chosen);

      const resumeAction = consumeSelfEvalResumeIfMatch(sportName, ageCategory);
      const filled = Object.values(chosen.entries || {}).some((entry) => String(entry.value || '').trim() !== '');
      if (resumeAction === 'submit' && filled && !viewOnly) {
        setPendingAction('submit');
        setConsentOpen(true);
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [hydrateKey, hasPreview, signedIn, user?.id, sportName, ageCategory, playerId, actingKind, ownerKey, viewOnly]);

  const gradeSyncKey = useMemo(
    () =>
      Object.entries(draft.entries || {})
        .map(([id, entry]) => `${id}:${entry?.value || ''}:${entry?.grade ?? ''}`)
        .sort()
        .join('|'),
    [draft.entries],
  );

  useEffect(() => {
    if (!allContests.length) return;
    const { draft: next, changes } = reassessSelfEvalDraftGrades(draft, allContests, ageCategory, genderKey);
    if (!changes.length) return;
    setDraft(next);
    setBenchmarkRevision(changes);
    if (ownerKey) persistContestSelfEvalForUser(ownerKey, next);
    else saveContestSelfEvalDraft(next);
    if (!signedIn || !user?.id || viewOnly) return;
    void (async () => {
      try {
        await api.put(
          `/contest-catalog/${encodeURIComponent(sportName)}/self-eval`,
          {
            ageCategory,
            genderKey,
            playerId: playerId || undefined,
            status: next.status === 'submitted' ? 'submitted' : 'saved',
            entries: next.entries,
            refreshGrades: true,
          },
          { skipErrorToast: true },
        );
        if (next.status === 'submitted') {
          const rows = Object.values(next.entries || {})
            .filter((entry) => catalogIds.includes(entry.contestId) && (String(entry.value || '').trim() !== '' || entry.grade))
            .map((entry) => {
              const contest = allContests.find((item) => item.id === entry.contestId);
              return {
                contestId: entry.contestId,
                skillSlug: entry.contestId,
                skillTitle: contest?.title || entry.contestId,
                grade: entry.grade,
                value: entry.value,
              };
            });
          if (rows.length) {
            await api.post(
              `/contest-catalog/${encodeURIComponent(sportName)}/self-eval-leaderboard/publish`,
              {
                sport: sportName,
                ageCategory,
                genderKey,
                playerId: playerId || undefined,
                entries: rows,
              },
              { skipErrorToast: true },
            );
          }
        }
      } catch {
        /* live grades still show locally */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gradeSyncKey fingerprints draft values/grades
  }, [allContests, ageCategory, genderKey, gradeSyncKey, hydrateKey, ownerKey, signedIn, user?.id, viewOnly, sportName, playerId, catalogIds]);

  const updateEntry = (contestId: string, patch: Partial<ContestSelfEvalEntry>) => {
    if (scorecardLocked) return;
    if (patch.recordedDate !== undefined) {
      const nextDate = String(patch.recordedDate || '').trim();
      if (
        nextDate &&
        countSelfEvalSkillsOnDate(draft.entries, nextDate, contestId, catalogIds) >= MAX_SELF_EVAL_SKILLS_PER_DATE
      ) {
        const warn = 'At most 2 skills can be evaluated on the same day considering child development';
        toast.warning(warn);
        return;
      }
    }
    setDraft((prev) => {
      const existing = prev.entries[contestId] || {
        contestId,
        value: '',
        recordedDate: '',
        selfNotes: '',
        coachNotes: '',
        errors: [],
        grade: null,
      };
      const nextEntry = { ...existing, ...patch, contestId };
      if (patch.value !== undefined) {
        const contest = allContests.find((c) => c.id === contestId);
        nextEntry.grade = contest ? assessEntryValue(contest, ageCategory, nextEntry.value, genderKey) : null;
        nextEntry.assessedAt = new Date().toISOString();
      }
      if (patch.value !== undefined || patch.recordedDate !== undefined || patch.errors !== undefined) {
        nextEntry.savedAt = undefined;
      }
      const next: ContestSelfEvalDraft = {
        ...prev,
        kind: 'contest_self_evaluation',
        sport: sportName,
        ageCategory,
        status: prev.status === 'submitted' ? prev.status : 'draft',
        entries: { ...prev.entries, [contestId]: nextEntry },
        updatedAt: new Date().toISOString(),
      };
      saveContestSelfEvalDraft(next, ownerKey || undefined);
      return next;
    });
  };

  const ensureAuthOrPrompt = (action: 'save' | 'submit'): boolean => {
    saveContestSelfEvalDraft({ ...draft, sport: sportName, ageCategory }, ownerKey || undefined);
    if (signedIn && user?.id) return true;
    rememberSelfEvalResume({ sport: sportName, ageCategory, action });
    setPendingAction(action);
    setAuthDialogOpen(true);
    toast.info(`Sign in to ${action === 'submit' ? 'submit' : 'save'} your self evaluation.`);
    return false;
  };

  const publishToCommunityStandings = async (nextDraft: ContestSelfEvalDraft) => {
    const entries = Object.values(nextDraft.entries || {})
      .filter((e) => catalogIds.includes(e.contestId) && (String(e.value || '').trim() !== '' || e.grade))
      .map((e) => {
        const contest = allContests.find((c) => c.id === e.contestId);
        return {
          contestId: e.contestId,
          skillSlug: e.contestId,
          skillTitle: contest?.title || e.contestId,
          grade: e.grade,
          value: e.value,
        };
      });
    if (!entries.length) return;
    try {
      await api.post(
        `/contest-catalog/${encodeURIComponent(sportName)}/self-eval-leaderboard/publish`,
        {
          sport: sportName,
          ageCategory,
          genderKey,
          playerId: playerId || undefined,
          entries,
        },
      );
    } catch (err) {
      console.warn('Self-eval community publish skipped/failed', err);
    }
  };

  const commitSelfEval = async (
    action: 'save' | 'submit',
    consent: ContestSelfEvalConsentRecord | undefined,
    contestId?: string | null,
  ) => {
    const now = new Date().toISOString();
    const catalogEntries = Object.fromEntries(
      catalogIds
        .map((id) => [id, draft.entries[id]] as const)
        .filter((row): row is [string, ContestSelfEvalEntry] => Boolean(row[1])),
    );
    if (action === 'save' && contestId && catalogEntries[contestId]) {
      catalogEntries[contestId] = { ...catalogEntries[contestId], savedAt: now };
    }
    if (action === 'submit') {
      catalogIds.forEach((id) => {
        if (!catalogEntries[id]) return;
        catalogEntries[id] = { ...catalogEntries[id], savedAt: catalogEntries[id].savedAt || now };
      });
    }
    const next: ContestSelfEvalDraft = {
      ...draft,
      sport: sportName,
      ageCategory,
      kind: 'contest_self_evaluation',
      lastConsent: consent,
      status: action === 'submit' ? 'submitted' : 'saved',
      submittedAt: action === 'submit' ? now : draft.submittedAt,
      entries: catalogEntries,
    };
    persistContestSelfEvalForUser(ownerKey || String(user!.id), next);
    setDraft(next);
    try {
      await api.put(
        `/contest-catalog/${encodeURIComponent(sportName)}/self-eval`,
        {
          ageCategory,
          genderKey,
          playerId: playerId || undefined,
          status: action === 'submit' ? 'submitted' : 'saved',
          entries: next.entries,
          lastConsent: consent,
        },
        { skipErrorToast: true },
      );
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const fail =
        axiosErr.response?.data?.message ||
        `Could not ${action} self-evaluation${displayPlayerName ? ` for ${displayPlayerName}` : ''}.`;
      toast.error(fail);
      return;
    }
    if (action === 'submit') {
      await publishToCommunityStandings(next);
      const cert = resolveFoundationCertification(
        next,
        allContests.map((c) => c.id),
      );
      const certNote = cert
        ? ` Certification level: ${cert.levelCode} (${cert.scorePercent}/100) — download from Certificates.`
        : '';
      toast.success(
        `Submitted ${skillCount} skill${skillCount === 1 ? '' : 's'} for ${ageCategory}. Added to the self-eval leaderboard.${certNote}`,
      );
      return;
    }
    const title = allContests.find((contest) => contest.id === contestId)?.title || 'Skill';
    toast.success(`Saved ${title}.`);
  };

  const handleSaveSkill = (contestId: string) => {
    if (viewOnly || lockedThisQuarter || missingDob) {
      if (missingDob) {
        toast.warning('Add a date of birth on this profile first. Age category is calculated from date of birth.');
      }
      return;
    }
    if (!canWriteThisAge) {
      toast.warning(ageMismatchMessage);
      return;
    }
    const entry = draft.entries[contestId];
    if (!isSelfEvalRowComplete(entry)) {
      toast.warning('Add a score and date before saving this skill.');
      return;
    }
    const date = String(entry.recordedDate || '').trim();
    if (countSelfEvalSkillsOnDate(draft.entries, date, contestId, catalogIds) >= MAX_SELF_EVAL_SKILLS_PER_DATE) {
      toast.warning('At most 2 skills can be evaluated on the same day considering child development');
      return;
    }
    if (!ensureAuthOrPrompt('save')) return;
    setPendingSaveContestId(contestId);
    setPendingAction('save');
    setConsentOpen(true);
  };

  const handleSubmit = () => {
    if (viewOnly) {
      toast.info('This scorecard is view-only. A parent can submit until you claim your email.');
      return;
    }
    if (missingDob) {
      toast.warning('Add a date of birth on this profile first. Age category is calculated from date of birth.');
      return;
    }
    if (lockedThisQuarter) {
      toast.warning(`Already submitted for ${currentQuarterLabel}`);
      return;
    }
    if (!canWriteThisAge) {
      toast.warning(ageMismatchMessage);
      return;
    }
    if (!allRowsSaved) {
      toast.warning('Save each skill, then submit.');
      return;
    }
    if (!ensureAuthOrPrompt('submit')) return;
    setPendingSaveContestId(null);
    setPendingAction('submit');
    setConsentOpen(true);
  };

  const handleConsentConfirm = (record: ContestSelfEvalConsentRecord) => {
    const action = record.action || pendingAction || 'save';
    const contestId = pendingSaveContestId;
    setConsentOpen(false);
    setPendingAction(null);
    setPendingSaveContestId(null);
    void commitSelfEval(action, record, contestId);
  };

  const handleClearDraft = () => {
    if (viewOnly || lockedThisQuarter) return;
    clearContestSelfEvalDraft(sportName, ageCategory, ownerKey || undefined);
    setDraft(createEmptyContestSelfEvalDraft(sportName, ageCategory));
    toast.info('Draft cleared for this age category.');
  };

  if (!hasPreview && !catalogLoading) {
    return <Alert severity="info">Self evaluation is not available for {sportName} yet.</Alert>;
  }

  if (catalogLoading && !hasContests) {
    return <Alert severity="info">Loading contest catalog…</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        <Box
          role="tablist"
          aria-label="Self evaluation views"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 0.5, md: 1 },
            minWidth: 0,
            flex: 1,
          }}
        >
          {(
            [
              { id: 'scorecard' as const, label: 'Scorecard' },
              { id: 'standings' as const, label: 'Standings & map' },
            ] as const
          ).map((item) => {
            const active = panel === item.id;
            return (
              <Box
                key={item.id}
                component="button"
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setPanel(item.id)}
                sx={{
                  appearance: 'none',
                  border: 'none',
                  borderBottom: active ? `3px solid ${brandColors.navy}` : '3px solid transparent',
                  bgcolor: 'transparent',
                  color: active ? brandColors.navy : brandColors.slate,
                  fontFamily: '"Sora", Arial, sans-serif',
                  fontWeight: active ? 800 : 600,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                  px: { xs: 1.25, md: 1.5 },
                  pt: 0.65,
                  pb: 1,
                  mb: '-2px',
                  cursor: 'pointer',
                  lineHeight: 1.3,
                  borderRadius: 0,
                  '&:hover': { color: brandColors.navy, bgcolor: 'transparent' },
                }}
              >
                {item.label}
              </Box>
            );
          })}
          <Button
            size="small"
            onClick={() => setCompareOpen(true)}
            startIcon={<CompareArrowsIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: brandColors.navy,
              mb: 0.5,
            }}
          >
            Compare with Contest
          </Button>
        </Box>
        {profileSlot ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.4,
              mb: 0.5,
              borderRadius: 1.5,
              bgcolor: '#efe6f8',
              border: `1px solid ${brandColors.violet}55`,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontWeight: 800,
                fontSize: '0.68rem',
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                color: brandColors.violet,
                whiteSpace: 'nowrap',
              }}
            >
              Profile
            </Typography>
            {profileSlot}
          </Box>
        ) : null}
      </Box>

      {panel === 'standings' ? (
        <ContestLeaderboardTabPanel
          sportName={sportName}
          ageCategory={ageCategory}
          mode="self_eval"
        />
      ) : (
        <>
          {benchmarkRevision && benchmarkRevision.length > 0 && (
            <Alert severity="success" sx={{ borderRadius: 2 }} onClose={() => setBenchmarkRevision(null)}>
              Benchmarks were updated. Grades were recalculated for{' '}
              {benchmarkRevision
                .slice(0, 4)
                .map((change) => {
                  const from = change.from || '—';
                  const to = change.to || '—';
                  return `${change.title} (${from} → ${to})`;
                })
                .join(', ')}
              {benchmarkRevision.length > 4 ? ` and ${benchmarkRevision.length - 4} more` : ''}. Certificates
              and standings now use the current targets.
            </Alert>
          )}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            At most 2 skills can be evaluated on the same day considering child development.
          </Alert>
          {viewOnly && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              View only{displayPlayerName ? ` — ${displayPlayerName}` : ''}. A parent must save and submit until you
              claim your email at the digital age of consent.
            </Alert>
          )}

          <ContestFrameShell
            tone="primary"
            integratedTitle={
              displayPlayerName ? `${foundationLabel} · ${displayPlayerName}` : foundationLabel
            }
            ageBadge={ageCategory}
            quarterBadge={currentQuarterLabel}
            titleAccent={brandColors.blue}
            frameHeight={{ xs: 'min(62vh, 520px)', md: 'min(68vh, 580px)', lg: 'calc(100dvh - 360px)' }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ContestSelfEvalScorecardPanel
                variant="self_eval"
                categoryGroups={categoryGroups}
                ageCategory={ageCategory}
                genderKey={genderKey}
                entries={draft.entries}
                onValueChange={(contestId, value) => updateEntry(contestId, { value })}
                onDateChange={(contestId, recordedDate) => updateEntry(contestId, { recordedDate })}
                onSelfNotesChange={(contestId, selfNotes) => updateEntry(contestId, { selfNotes })}
                onErrorsChange={(contestId, errors) => updateEntry(contestId, { errors })}
                onSaveSkill={handleSaveSkill}
                readOnly={scorecardLocked}
              />
            </Box>
          </ContestFrameShell>

          <Paper sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="flex-end"
              alignItems={{ sm: 'center' }}
            >
              {saveDisabledReason ? (
                <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 700, mr: 'auto' }}>
                  {saveDisabledReason}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {signedIn && (
                  <Button onClick={() => setCertificateOpen(true)} sx={{ textTransform: 'none' }}>
                    View certificate
                  </Button>
                )}
                {!viewOnly && (
                  <>
                    <Button
                      onClick={handleClearDraft}
                      disabled={lockedThisQuarter}
                      sx={{ textTransform: 'none' }}
                    >
                      Clear draft
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={() => void handleSubmit()}
                      disabled={!canWriteThisAge || lockedThisQuarter || !allRowsSaved}
                      sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
                    >
                      Submit{displayPlayerName ? ` · ${displayPlayerName}` : ''}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </Paper>
        </>
      )}

      <Dialog open={authDialogOpen} onClose={() => setAuthDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sign in to {pendingAction === 'submit' ? 'submit' : 'save'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your values stay on this device until you sign in. After login we load any scorecard
            already saved to your account, or keep this draft if the account has none yet.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAuthDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Keep editing
          </Button>
          <Button
            variant="contained"
            startIcon={<LoginIcon />}
            onClick={() => {
              saveContestSelfEvalDraft({ ...draft, sport: sportName, ageCategory }, ownerKey || undefined);
              rememberSelfEvalResume({
                sport: sportName,
                ageCategory,
                action: pendingAction || 'save',
              });
              onLoginRequired?.(loginReturnPath);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
          >
            Sign in
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={certificateOpen}
        onClose={() => setCertificateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: brandColors.white } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: brandColors.navy, pr: 2 }}>
          Technical Foundation Certificate
        </DialogTitle>
        <DialogContent>
          {foundationCert || recordedScoreRows.length ? (
            <Box
              sx={{
                border: `2px solid ${brandColors.blue}`,
                borderRadius: 2,
                p: { xs: 2, md: 3.5 },
                textAlign: 'center',
                background: brandColors.white,
                boxShadow: 'inset 0 0 0 1px #dbe4f5',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', md: '1.45rem' }, color: brandColors.navy }}>
                Technical Foundation Certificate
              </Typography>
              <Typography sx={{ color: brandColors.blue, fontWeight: 700, mt: 0.75 }}>
                {foundationCert?.levelLabel || `${sportName} · ${ageCategory}`}
              </Typography>
              <Typography sx={{ color: brandColors.slate, mt: 2 }}>This is to certify that</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color: brandColors.navy, mt: 0.75 }}>
                {certificatePlayer.firstName}
              </Typography>
              <Typography sx={{ color: brandColors.slate, fontSize: '0.85rem', mt: 0.5 }}>
                Player ID: {certificatePlayer.playerIdCode}
              </Typography>
              {foundationCert ? (
                <>
                  <Typography sx={{ mt: 2, color: brandColors.navy }}>
                    has achieved{' '}
                    <Chip
                      size="small"
                      label={foundationCert.levelCode}
                      sx={{
                        fontWeight: 900,
                        mx: 0.4,
                        bgcolor: getGradeColor(foundationCert.levelCode).bg,
                        color: getGradeColor(foundationCert.levelCode).text,
                        border: `1px solid ${getGradeColor(foundationCert.levelCode).border}`,
                      }}
                    />{' '}
                    level in {sportName} Technical Foundation ({ageCategory}).
                  </Typography>
                  <Typography sx={{ fontWeight: 800, mt: 2, color: brandColors.navy }}>
                    Composite score: {foundationCert.scorePercent.toFixed(1)} / 100
                  </Typography>
                  <Typography sx={{ color: brandColors.slate, mt: 0.75 }}>
                    Skills assessed: {foundationCert.gradedCount}
                    {foundationCert.skillCount ? ` / ${foundationCert.skillCount}` : ''}
                  </Typography>
                  <Typography sx={{ color: brandColors.slate, fontSize: '0.82rem', mt: 1.5 }}>
                    Level is based on the minimum grade across Foundation skills.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ mt: 2, color: brandColors.navy }}>
                    has recorded {recordedScoreRows.length} skill{recordedScoreRows.length === 1 ? '' : 's'} for {sportName}{' '}
                    Technical Foundation ({ageCategory}).
                  </Typography>
                  <Stack spacing={0.4} sx={{ mt: 1.5, textAlign: 'left', maxWidth: 420, mx: 'auto' }}>
                    {recordedScoreRows.map((row) => (
                      <Typography key={row.contest.id} sx={{ color: brandColors.slate, fontSize: '0.85rem' }}>
                        {row.contest.title}: {row.entry?.value}
                        {row.entry?.grade ? ` · Grade ${row.entry.grade}` : ' · grade pending benchmarks'}
                      </Typography>
                    ))}
                  </Stack>
                  <Typography sx={{ color: brandColors.slate, fontSize: '0.82rem', mt: 1.5 }}>
                    Certificate level will appear when grade ranges are published. Saved scores stay and the
                    certificate updates automatically.
                  </Typography>
                </>
              )}
              <Typography sx={{ color: brandColors.slate, fontSize: '0.78rem', mt: 2 }}>
                Issued by EncycGames ·{' '}
                {new Date(foundationCert?.updatedAt || draft.updatedAt || Date.now()).toLocaleDateString()}
                {foundationCert
                  ? ` · ${foundationCertificateNo(foundationCert, certificatePlayer.playerIdCode)}`
                  : ''}
              </Typography>
            </Box>
          ) : (
            <Alert severity="info">
              Save and submit self-evaluation scores to issue a Technical Foundation certificate.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCertificateOpen(false)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          {foundationCert ? (
            <Button
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={handleDownloadFoundationCert}
              sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
            >
              Download PDF
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      {consentOpen ? (
        <ContestSelfEvalConsentDialog
          key={`${pendingAction || 'save'}-${pendingSaveContestId || 'all'}-${consentSkills.map((skill) => skill.contestId).join('|')}`}
          open
          action={pendingAction === 'submit' ? 'submit' : 'save'}
          sportName={sportName}
          ageCategory={ageCategory}
          role={consentRole}
          skills={consentSkills}
          guidelineContest={guidelineContest}
          playerName={displayPlayerName}
          onCancel={() => {
            setConsentOpen(false);
            setPendingAction(null);
            setPendingSaveContestId(null);
          }}
          onConfirm={handleConsentConfirm}
        />
      ) : null}
      <ContestResultsCompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        playerName={displayPlayerName}
        ageCategory={ageCategory}
        rows={compareRows}
      />
    </Box>
  );
};

export default ContestSelfEvalTabPanel;
